import { Link, Outlet, useLocation } from 'react-router'
import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/cn.ts'

export default function DocsLayout() {
	const location = useLocation()
	const currentSection = findCurrentSection(location.pathname)

	return (
		<div className="container mx-auto px-4 py-10">
			<div className="flex gap-0">
				{/* Sidebar */}
				<aside className="hidden w-56 shrink-0 md:block">
					<nav className="sticky top-10">
						<DocsNav pathname={location.pathname} />
					</nav>
				</aside>

				{/* Main content */}
				<main className="border-border min-w-0 flex-1 md:border-l md:pl-10">
					<details className="border-border bg-card mb-8 rounded-lg border md:hidden">
						<summary className="text-foreground cursor-pointer px-4 py-2.5 text-sm font-medium">
							Docs menu
						</summary>
						<nav className="border-border border-t px-4 py-4">
							<DocsNav pathname={location.pathname} />
						</nav>
					</details>

					{currentSection ? (
						<p className="text-primary mb-1 text-xs font-medium tracking-wider uppercase">
							{currentSection}
						</p>
					) : null}
					<article className="prose dark:prose-invert max-w-3xl">
						<Outlet />
					</article>
					<PrevNextLinks pathname={location.pathname} />
				</main>
			</div>
		</div>
	)
}

const navSections = [
	{
		label: 'Overview',
		links: [
			{ to: '/docs', title: 'Introduction' },
			{ to: '/docs/getting-started', title: 'Getting Started' },
		],
	},
	{
		label: 'Guides',
		links: [
			{ to: '/docs/ai-diagnosis', title: 'AI Diagnosis' },
			{ to: '/docs/scheduling', title: 'Scheduling Audits' },
			{ to: '/docs/secure-pages', title: 'Auditing Secure Pages' },
			{ to: '/docs/github-integration', title: 'GitHub Integration' },
			{ to: '/docs/notifications', title: 'Notifications' },
			{ to: '/docs/webhooks', title: 'Webhooks' },
			{ to: '/docs/api-usage', title: 'API & CLI' },
			{ to: '/docs/mcp', title: 'MCP Server' },
		],
	},
	{
		label: 'Reference',
		links: [
			{ to: '/docs/concepts', title: 'Concepts' },
			{ to: '/docs/regions-and-devices', title: 'Regions & Devices' },
			{ to: '/docs/audit-stability', title: 'Audit Stability' },
			{ to: '/docs/api', title: 'API Reference' },
			{ to: '/docs/usage-and-billing', title: 'Usage & Billing' },
		],
	},
	{
		label: 'Best Practices',
		links: [
			{ to: '/docs/performance-monitoring', title: 'Performance Monitoring' },
			{ to: '/docs/security', title: 'Security' },
		],
	},
]

const allLinks = navSections.flatMap((section) => section.links)

function DocsNav({ pathname }: { pathname: string }) {
	return (
		<div className="space-y-6">
			{navSections.map((section) => (
				<div key={section.label}>
					<p className="text-foreground mb-2 px-3 text-xs font-medium tracking-wider uppercase">
						{section.label}
					</p>
					<ul className="border-border ml-3 border-l">
						{section.links.map((link) => {
							const isActive = pathname === link.to

							return (
								<li key={link.to}>
									<Link
										to={link.to}
										prefetch="intent"
										className={cn(
											'-ml-px block border-l py-1.5 pr-3 pl-4 text-sm transition-colors',
											isActive
												? 'border-primary text-foreground font-medium'
												: 'text-muted-foreground hover:border-border hover:text-foreground border-transparent',
										)}
									>
										{link.title}
									</Link>
								</li>
							)
						})}
					</ul>
				</div>
			))}
		</div>
	)
}

function PrevNextLinks({ pathname }: { pathname: string }) {
	const index = allLinks.findIndex((link) => link.to === pathname)
	if (index === -1) return null

	const prev = allLinks[index - 1]
	const next = allLinks[index + 1]

	return (
		<nav className="border-border mt-12 flex max-w-3xl justify-between gap-4 border-t pt-6">
			{prev ? (
				<Link to={prev.to} prefetch="intent" className="group text-sm">
					<span className="text-muted-foreground mb-1 block text-xs">
						Previous
					</span>
					<span className="text-foreground group-hover:text-primary flex items-center gap-1.5 font-medium transition-colors">
						<Icon name="arrow-left" size="sm" />
						{prev.title}
					</span>
				</Link>
			) : (
				<span />
			)}
			{next ? (
				<Link
					to={next.to}
					prefetch="intent"
					className="group text-right text-sm"
				>
					<span className="text-muted-foreground mb-1 block text-xs">Next</span>
					<span className="text-foreground group-hover:text-primary flex items-center gap-1.5 font-medium transition-colors">
						{next.title}
						<Icon name="arrow-right" size="sm" />
					</span>
				</Link>
			) : (
				<span />
			)}
		</nav>
	)
}

function findCurrentSection(pathname: string) {
	for (const section of navSections) {
		for (const link of section.links) {
			if (link.to === pathname) {
				return section.label
			}
		}
	}
	return null
}
